import { computed, type Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  type WritableStateSource,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { readVaultItemsPerPageFromPreferences } from '@nebula/shared-vault/data-access';
import type { RepositoryListItem } from 'libs/vault/src/lib/domain/src/lib/repository/repository.model';
import {
  buildRepositoryAdminPersistConfig,
  clearLegacyRepositoryAdminPersistState,
} from './repository-admin-persist.util';
import {
  clearPersistedState,
  loadPersistedState,
  savePersistedState,
  type PersistConfig,
} from './signal-store-persist.util';

type RepositoryViewMode = 'table' | 'explorer';

interface RepositoryAdminStoreState {
  loading: boolean;
  loadingAction: boolean;
  error: string | null;
  info: string | null;
  currentPath: string;
  searchTerm: string;
  fileTypeFilter: string;
  searchInZips: boolean;
  currentZipPath: string | null;
  zipInternalPath: string;
  rows: RepositoryListItem[];
  selectedFiles: string[];
  tablePageSize: number;
  tablePageIndex: number;
  tableSortColumn: keyof RepositoryListItem | string | null;
  tableSortDirection: 'asc' | 'desc' | null;
  viewMode: RepositoryViewMode;
  explorerChildCache: Record<string, RepositoryListItem[]>;
  explorerExpandedPaths: string[];
  selectedExplorerPath: string | null;
  activeAssignmentEntryPath: string;
}

type RepositoryAdminPersistedState = Pick<
  RepositoryAdminStoreState,
  | 'currentPath'
  | 'searchTerm'
  | 'fileTypeFilter'
  | 'currentZipPath'
  | 'zipInternalPath'
  | 'tablePageSize'
  | 'tablePageIndex'
  | 'tableSortColumn'
  | 'tableSortDirection'
  | 'explorerExpandedPaths'
  | 'selectedExplorerPath'
  | 'activeAssignmentEntryPath'
>;

let activePersistConfig: PersistConfig = buildRepositoryAdminPersistConfig('', '');

function isRepositoryAdminPersistedState(
  value: unknown,
): value is RepositoryAdminPersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const sortDirectionOk =
    v['tableSortDirection'] === 'asc' ||
    v['tableSortDirection'] === 'desc' ||
    v['tableSortDirection'] === null;
  return (
    typeof v['currentPath'] === 'string' &&
    typeof v['searchTerm'] === 'string' &&
    typeof v['fileTypeFilter'] === 'string' &&
    (typeof v['currentZipPath'] === 'string' || v['currentZipPath'] === null) &&
    typeof v['zipInternalPath'] === 'string' &&
    typeof v['tablePageSize'] === 'number' &&
    typeof v['tablePageIndex'] === 'number' &&
    (typeof v['tableSortColumn'] === 'string' || v['tableSortColumn'] === null) &&
    sortDirectionOk &&
    Array.isArray(v['explorerExpandedPaths'])
    && (typeof v['selectedExplorerPath'] === 'string' || v['selectedExplorerPath'] === null)
    && typeof v['activeAssignmentEntryPath'] === 'string'
  );
}

const initialState: RepositoryAdminStoreState = {
  loading: false,
  loadingAction: false,
  error: null,
  info: null,
  currentPath: '',
  searchTerm: '',
  fileTypeFilter: 'all',
  searchInZips: false,
  currentZipPath: null,
  zipInternalPath: '',
  rows: [],
  selectedFiles: [],
  tablePageSize: readVaultItemsPerPageFromPreferences(),
  tablePageIndex: 0,
  tableSortColumn: 'name',
  tableSortDirection: 'asc',
  viewMode: 'table',
  explorerChildCache: {},
  explorerExpandedPaths: [],
  selectedExplorerPath: null,
  activeAssignmentEntryPath: '',
};

function readScopedPersistedState(): Partial<RepositoryAdminPersistedState> {
  return (
    loadPersistedState(activePersistConfig, isRepositoryAdminPersistedState) ?? {}
  );
}

function mergePersistedIntoInitialState(): RepositoryAdminStoreState {
  return {
    ...initialState,
    ...readScopedPersistedState(),
    searchInZips: false,
    viewMode: 'table',
  };
}

type RepositoryStoreLike = WritableStateSource<RepositoryAdminStoreState> & {
  currentPath: Signal<string>;
  searchTerm: Signal<string>;
  fileTypeFilter: Signal<string>;
  searchInZips: Signal<boolean>;
  currentZipPath: Signal<string | null>;
  zipInternalPath: Signal<string>;
  tablePageSize: Signal<number>;
  tablePageIndex: Signal<number>;
  tableSortColumn: Signal<keyof RepositoryListItem | string | null>;
  tableSortDirection: Signal<'asc' | 'desc' | null>;
  viewMode: Signal<RepositoryViewMode>;
  explorerExpandedPaths: Signal<string[]>;
  selectedExplorerPath: Signal<string | null>;
  activeAssignmentEntryPath: Signal<string>;
};

export const repositoryAdminStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    explorerExpandedSet: computed(() => new Set(state.explorerExpandedPaths())),
  })),
  withMethods((store) => ({
    bindPersistScope(tenantId: string, userId: string): boolean {
      const nextConfig = buildRepositoryAdminPersistConfig(tenantId, userId);
      const scopeChanged = nextConfig.key !== activePersistConfig.key;
      if (!scopeChanged) {
        return false;
      }
      clearLegacyRepositoryAdminPersistState();
      activePersistConfig = nextConfig;
      patchState(store, mergePersistedIntoInitialState());
      return true;
    },
    setViewMode(mode: RepositoryViewMode): void {
      patchState(store, { viewMode: mode });
      persistState(store as RepositoryStoreLike);
    },
    setSearchTerm(value: string): void {
      patchState(store, { searchTerm: value ?? '' });
      persistState(store as RepositoryStoreLike);
    },
    setFileTypeFilter(value: string): void {
      patchState(store, { fileTypeFilter: value || 'all' });
      persistState(store as RepositoryStoreLike);
    },
    setSearchInZips(value: boolean): void {
      patchState(store, { searchInZips: !!value });
      persistState(store as RepositoryStoreLike);
    },
    setCurrentPath(value: string): void {
      patchState(store, { currentPath: value ?? '' });
      persistState(store as RepositoryStoreLike);
    },
    setCurrentZipPath(value: string | null): void {
      patchState(store, { currentZipPath: value });
      persistState(store as RepositoryStoreLike);
    },
    setZipInternalPath(value: string): void {
      patchState(store, { zipInternalPath: value ?? '' });
      persistState(store as RepositoryStoreLike);
    },
    setTableState(value: {
      pageSize?: number;
      pageIndex?: number;
      sortColumn?: keyof RepositoryListItem | string | null;
      sortDirection?: 'asc' | 'desc' | null;
    }): void {
      patchState(store, {
        tablePageSize: value.pageSize ?? store.tablePageSize(),
        tablePageIndex: value.pageIndex ?? store.tablePageIndex(),
        tableSortColumn: value.sortColumn ?? store.tableSortColumn(),
        tableSortDirection: value.sortDirection ?? store.tableSortDirection(),
      });
      persistState(store as RepositoryStoreLike);
    },
    setExplorerExpandedPaths(paths: string[]): void {
      patchState(store, { explorerExpandedPaths: Array.from(new Set(paths)) });
      persistState(store as RepositoryStoreLike);
    },
    setSelectedExplorerPath(path: string | null): void {
      patchState(store, { selectedExplorerPath: path });
      persistState(store as RepositoryStoreLike);
    },
    setActiveAssignmentEntryPath(path: string): void {
      patchState(store, { activeAssignmentEntryPath: path ?? '' });
      persistState(store as RepositoryStoreLike);
    },
    toggleExplorerExpandedPath(path: string): void {
      const set = new Set(store.explorerExpandedPaths());
      if (set.has(path)) set.delete(path);
      else set.add(path);
      patchState(store, { explorerExpandedPaths: Array.from(set) });
      persistState(store as RepositoryStoreLike);
    },
    clearExplorerState(): void {
      patchState(store, { explorerChildCache: {}, explorerExpandedPaths: [] });
      persistState(store as RepositoryStoreLike);
    },
    clearExplorerCache(): void {
      patchState(store, { explorerChildCache: {} });
    },
    setExplorerChildren(path: string, rows: RepositoryListItem[]): void {
      patchState(store, {
        explorerChildCache: {
          ...store.explorerChildCache(),
          [path]: rows,
        },
      });
    },
    setRows(rows: RepositoryListItem[]): void {
      patchState(store, { rows, selectedFiles: [] });
    },
    setSelectedFiles(paths: string[]): void {
      patchState(store, { selectedFiles: paths });
    },
    setLoading(value: boolean): void {
      patchState(store, { loading: value });
    },
    setLoadingAction(value: boolean): void {
      patchState(store, { loadingAction: value });
    },
    setError(value: string | null): void {
      patchState(store, { error: value });
    },
    setInfo(value: string | null): void {
      patchState(store, { info: value });
    },
    restoreRoot(): void {
      patchState(store, {
        currentZipPath: null,
        zipInternalPath: '',
        currentPath: '',
        selectedExplorerPath: null,
      });
      persistState(store as RepositoryStoreLike);
    },
    resetNavigationState(): void {
      patchState(store, {
        currentZipPath: null,
        zipInternalPath: '',
        currentPath: '',
        selectedExplorerPath: null,
        activeAssignmentEntryPath: '',
        searchTerm: '',
        fileTypeFilter: 'all',
        searchInZips: false,
        explorerChildCache: {},
        explorerExpandedPaths: [],
      });
      persistState(store as RepositoryStoreLike);
    },
    clearPersistedState(): void {
      clearPersistedState(activePersistConfig);
      patchState(store, initialState);
    },
  })),
);

function persistState(store: RepositoryStoreLike): void {
  const state: RepositoryAdminPersistedState = {
    currentPath: store.currentPath(),
    searchTerm: store.searchTerm(),
    fileTypeFilter: store.fileTypeFilter(),
    currentZipPath: store.currentZipPath(),
    zipInternalPath: store.zipInternalPath(),
    tablePageSize: store.tablePageSize(),
    tablePageIndex: store.tablePageIndex(),
    tableSortColumn: store.tableSortColumn(),
    tableSortDirection: store.tableSortDirection(),
    explorerExpandedPaths: store.explorerExpandedPaths(),
    selectedExplorerPath: store.selectedExplorerPath(),
    activeAssignmentEntryPath: store.activeAssignmentEntryPath(),
  };
  savePersistedState(activePersistConfig, state);
}

export type RepositoryAdminStore = typeof repositoryAdminStore;
